// Tipos base
export interface BaseEntity {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// Tipos de usuario y autenticación
export interface User extends BaseEntity {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt?: string;
}

/** Filtros para listado de personal (Admin) */
export interface StaffListFilters {
  page?: number;
  pageSize?: number;
  role?: UserRole;
  isActive?: boolean;
  search?: string;
}

/** Formulario crear usuario (Personal) */
export interface CreateStaffUserForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role: UserRole;
}

/** Formulario editar usuario (sin email) */
export interface UpdateStaffUserForm {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  role?: UserRole;
  isActive?: boolean;
  password?: string;
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  COORDINATOR = 'COORDINATOR',
  DIRECTOR = 'DIRECTOR',
  REGISTRY = 'REGISTRY',
  PSYCHOLOGY = 'PSYCHOLOGY',
  SECRETARY = 'SECRETARY',
  FINANCE = 'FINANCE',
  INVENTORY = 'INVENTORY',
  TEACHER = 'TEACHER',
  VIEWER = 'VIEWER',
}

// Tipos de año académico
export interface AcademicYear extends BaseEntity {
  name: string;
  code: string;
  startYear: number;
  endYear: number;
  state: YearState;
  dueDay: number;
  graceDays1: number;
  fee1: number;
  days2: number;
  fee2: number;
  shiftFullOn: boolean;
  shiftHalfOn: boolean;
  grades?: Grade[];
  priceRules?: PriceRule[];
}

export enum YearState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
}

// Tipos de grados y secciones
export interface Grade extends BaseEntity {
  yearId: string;
  band: GradeBand;
  level: GradeLevel;
  active: boolean;
  sections?: Section[];
}

export enum GradeBand {
  INICIAL = 'INICIAL',
  PRIMARIA = 'PRIMARIA',
  SECUNDARIA = 'SECUNDARIA',
}

export enum GradeLevel {
  // Inicial
  PARVULO_1 = 'PARVULO_1',
  PARVULO_2 = 'PARVULO_2',
  PRE_KINDER = 'PRE_KINDER',
  KINDER = 'KINDER',
  PRE_PRIMARIO = 'PRE_PRIMARIO',

  // Primaria
  PRIMARIA_1 = 'PRIMARIA_1',
  PRIMARIA_2 = 'PRIMARIA_2',
  PRIMARIA_3 = 'PRIMARIA_3',
  PRIMARIA_4 = 'PRIMARIA_4',
  PRIMARIA_5 = 'PRIMARIA_5',
  PRIMARIA_6 = 'PRIMARIA_6',

  // Secundaria
  SECUNDARIA_1 = 'SECUNDARIA_1',
  SECUNDARIA_2 = 'SECUNDARIA_2',
  SECUNDARIA_3 = 'SECUNDARIA_3',
  SECUNDARIA_4 = 'SECUNDARIA_4',
  SECUNDARIA_5 = 'SECUNDARIA_5',
  SECUNDARIA_6 = 'SECUNDARIA_6',
}

export interface Section extends BaseEntity {
  yearId: string;
  gradeId: string;
  letter: string;
  active: boolean;
}

// Tipos de precios
export interface PriceRule extends BaseEntity {
  yearId: string;
  gradeLevel: GradeLevel;
  shift: ShiftType;
  tuition: number;
  enrollment: number;
}

export enum ShiftType {
  COMPLETA = 'COMPLETA',
  MEDIA = 'MEDIA',
}

// Tipos de respuesta de la API
export interface Pagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
  // Nota: varios endpoints devuelven paginación en el nivel superior.
  pagination?: Pagination;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: Pagination;
}

// Tipos para formularios
export interface CreateAcademicYearForm {
  name: string;
  startYear: number;
  endYear: number;
  autoGenerate: boolean;
  billingPolicy: {
    dueDay: number;
    graceDays1: number;
    fee1: string;
    days2: number;
    fee2: string;
  };
  tandas: {
    completa: boolean;
    media: boolean;
  };
}

export interface UpdateBillingPolicyForm {
  dueDay: number;
  graceDays1: number;
  fee1: string;
  days2: number;
  fee2: string;
}

export interface PricingForm {
  [key: string]: {
    completa: {
      tuition: string;
      enrollment: string;
    };
    media: {
      tuition: string;
      enrollment: string;
    };
  };
}

// Tipos para filtros
export interface AcademicYearFilters {
  state?: YearState;
  search?: string;
  page?: number;
  limit?: number;
}

// Tipos para el contexto de autenticación
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (data: { firstName: string; lastName: string; email: string; phone?: string }) => Promise<void>;
  changePassword: (data: { currentPassword: string; newPassword: string }) => Promise<void>;
}

// Tipos para notificaciones
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}
